import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { AssetStatusBadge, ConditionBadge } from '@/components/shared/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Cpu, Search, Filter, Wrench } from 'lucide-react';
import { useAssets } from '@/hooks/useAssets';
import { Skeleton } from '@/components/ui/skeleton';
import ProcessAssetDialog from '@/components/processing/ProcessAssetDialog';
import type { Asset, AssetStatus } from '@/types/models';

export default function ProcessingQueuePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const { data: allAssets = [], isLoading } = useAssets();

  // Only show non-completed assets in processing queue
  const processingStatuses: AssetStatus[] = ['received', 'processing', 'testing', 'triaged', 'sanitization_pending', 'sanitized', 'qa_passed', 'inventory', 'disposition'];

  const filtered = allAssets.filter((a) => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (!processingStatuses.includes(a.status) && statusFilter === 'all') return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        a.internal_barcode.toLowerCase().includes(q) ||
        (a.serial_number?.toLowerCase().includes(q)) ||
        (a.category_name?.toLowerCase().includes(q)) ||
        (a.manufacturer_name?.toLowerCase().includes(q))
      );
    }
    return true;
  });

  function handleAssetUpdated(_updated: Asset) {
    // Query cache is invalidated by the mutation hook — no local state needed
  }

  const statusCounts = {
    received: allAssets.filter((a) => a.status === 'received').length,
    processing: allAssets.filter((a) => a.status === 'processing').length,
    testing: allAssets.filter((a) => a.status === 'testing').length,
    completed: allAssets.filter((a) => a.status === 'completed').length,
  };

  return (
    <>
      <PageHeader title="Processing Queue" description="Inspect, test, and process assets" icon={Cpu} />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Received', count: statusCounts.received, color: 'text-[hsl(var(--status-received))]' },
          { label: 'Processing', count: statusCounts.processing, color: 'text-[hsl(var(--status-processing))]' },
          { label: 'Testing', count: statusCounts.testing, color: 'text-[hsl(var(--status-testing))]' },
          { label: 'Completed', count: statusCounts.completed, color: 'text-[hsl(var(--status-complete))]' },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.count}</p>
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by barcode, serial, category..."
                className="pl-9"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="testing">Testing</SelectItem>
              <SelectItem value="triaged">Triaged</SelectItem>
              <SelectItem value="sanitized">Sanitized</SelectItem>
              <SelectItem value="qa_passed">QA Passed</SelectItem>
              <SelectItem value="inventory">Inventory</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Barcode</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((asset) => (
                <TableRow key={asset.id} className="cursor-pointer" onClick={() => setSelectedAsset(asset)}>
                  <TableCell className="font-mono text-sm font-medium">{asset.internal_barcode}</TableCell>
                  <TableCell className="text-sm">{asset.category_name}</TableCell>
                  <TableCell className="text-sm">{asset.manufacturer_name || '—'}</TableCell>
                  <TableCell className="text-sm">{asset.model || '—'}</TableCell>
                  <TableCell><ConditionBadge condition={asset.condition} /></TableCell>
                  <TableCell><AssetStatusBadge status={asset.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAsset(asset);
                      }}
                    >
                      <Wrench className="h-3.5 w-3.5" /> Process
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No assets found in processing queue
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ProcessAssetDialog
        open={!!selectedAsset}
        onOpenChange={(open) => !open && setSelectedAsset(null)}
        asset={selectedAsset}
        onUpdated={handleAssetUpdated}
      />
    </>
  );
}
